import React from 'react';
import { Button, Flex, Input, Space, Tag, Upload, theme } from 'antd';
import { ArrowUpOutlined, PaperClipOutlined } from '@ant-design/icons';
import './widget-messenger.css';

export interface ComposerPillProps {
    draft: string;
    onDraftChange: (value: string) => void;
    onSend: () => void;
    onTyping: () => void;
    onUpload: (file: File) => Promise<boolean>;
    pendingAttachmentIds: number[];
    onRemoveAttachment: (id: number) => void;
    conversationStarted: boolean;
    isSending: boolean;
    uploading: boolean;
    placeholder: string;
    attachLabel: string;
    sendLabel: string;
    poweredByLabel: string;
}

/**
 * Intercom-style pill composer with inline attach and circular send button.
 */
export const ComposerPill: React.FC<ComposerPillProps> = ({
    draft,
    onDraftChange,
    onSend,
    onTyping,
    onUpload,
    pendingAttachmentIds,
    onRemoveAttachment,
    conversationStarted,
    isSending,
    uploading,
    placeholder,
    attachLabel,
    sendLabel,
    poweredByLabel,
}) => {
    const { token } = theme.useToken();
    const canSend =
        !isSending && (draft.trim().length > 0 || pendingAttachmentIds.length > 0);

    return (
        <div className="support-messenger-footer">
            {pendingAttachmentIds.length > 0 ? (
                <Space size="small" wrap style={{ marginBottom: 8 }}>
                    {pendingAttachmentIds.map((id) => (
                        <Tag
                            key={id}
                            icon={<PaperClipOutlined />}
                            closable
                            onClose={() => onRemoveAttachment(id)}
                        >
                            #{id}
                        </Tag>
                    ))}
                </Space>
            ) : null}
            <Flex
                className="support-messenger-composer-pill"
                align="flex-end"
                style={{
                    border: `1px solid ${token.colorBorder}`,
                    background: token.colorBgElevated,
                }}
            >
                <Upload
                    multiple
                    showUploadList={false}
                    disabled={!conversationStarted}
                    beforeUpload={(file) => {
                        void onUpload(file as unknown as File);
                        return false;
                    }}
                >
                    <Button
                        type="text"
                        size="small"
                        icon={<PaperClipOutlined />}
                        loading={uploading}
                        disabled={!conversationStarted}
                        aria-label={attachLabel}
                        title={attachLabel}
                    />
                </Upload>
                <Input.TextArea
                    className="support-messenger-composer-input"
                    variant="borderless"
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    placeholder={placeholder}
                    value={draft}
                    onChange={(e) => {
                        onDraftChange(e.target.value);
                        if (e.target.value.trim() && conversationStarted) {
                            onTyping();
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (canSend) {
                                onSend();
                            }
                        }
                    }}
                    disabled={isSending}
                />
                <Button
                    className="support-messenger-composer-send"
                    type="primary"
                    shape="circle"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    loading={isSending}
                    disabled={!canSend}
                    onClick={onSend}
                    aria-label={sendLabel}
                    title={sendLabel}
                />
            </Flex>
            <div
                className="support-messenger-branding"
                style={{ color: token.colorTextTertiary }}
            >
                {poweredByLabel}
            </div>
        </div>
    );
};
